/**
 * The machine-readable codes the Travel Desk API puts in an error's `details.code` (ADR 0030):
 * `TRAVEL_TRIP_ERROR_CODES` (trips, profile, policy), `TRAVEL_DESK_ERROR_CODES` (bookings, options,
 * amendments, vendors, credits, documents) and their union `TRAVEL_ERROR_CODES`.
 *
 * A screen branches on the code, never on the message. The API sends the code as the error's
 * details object directly — `new ConflictError(message, { code: 'stale_version' })` — so the
 * client reads `error.details.code`. Some codes add fields beside it (`illegal_trip_transition`
 * carries `from` and `to`). Pure and RN-safe.
 *
 * Status in brackets is the HTTP status the code is sent with.
 */
export const TRAVEL_TRIP_ERROR_CODES = [
  /** [404] Travel Desk is switched off for this organisation (D33). Every `/travel` route except
   * the module status and the settings answers this. */
  'module_disabled',
  /** [409] The `version` sent is not the row's current one; re-read and retry (EC-51). */
  'stale_version',
  /** [409] Another trip of the same traveller already covers these dates and destination. */
  'duplicate_trip',
  /** [409] The status move is not in `TRIP_TRANSITIONS`. Carries `from` and `to`. */
  'illegal_trip_transition',
  /** [409] Another policy version of the same scope already covers `validFrom`. */
  'policy_overlap',
  /** [409] The policy version is in force or past; only `validTo` may change. */
  'policy_in_force',
  /** [422] The trip starts before today (in the location's timezone) and `isPostFacto` is false. */
  'start_in_past',
  /** [422] `isPostFacto` is true but the trip has not started yet. */
  'post_facto_not_started',
  /** [422] The post-facto trip began longer ago than the policy allows. */
  'post_facto_too_old',
  /** [422] The trip ends before it starts. */
  'end_before_start',
  /** [422] The policy asks for a justification and none was given. */
  'justification_required',
  /** [422] A personal day falls outside the trip's dates. */
  'personal_day_outside_trip',
  /** [422] A co-traveller is unknown, inactive, in another organisation, the requester, or listed twice. */
  'invalid_co_traveller',
  /** [422] A personal day names a leave request that is not the traveller's own. */
  'invalid_leave_request',
  /** [422] A visa id in a profile update does not belong to the caller's profile. */
  'visa_not_found',
  /** [422] Submit refuses: a personal day has no leave covering it. Carries `dates` (the days without leave). */
  'personal_day_leave_required',
] as const;

/**
 * The desk and traveller-side booking codes (bookings, options, amendments, vendors, credits,
 * refunds, documents). Folded into `TRAVEL_ERROR_CODES` below; the API's
 * `lib/travel/desk/error-codes.ts` re-exports them from here.
 */
export const TRAVEL_DESK_ERROR_CODES = [
  /** [409] The booking is not in a state this action works on (confirmed, cancelled, ...). */
  'booking_not_open',
  /** [409] The booking is held for a manager's decision (D11, EC-20) and cannot be acted on yet. */
  'booking_on_hold',
  /** [409] The trip is not open for the desk (rejected, withdrawn, closed, ...). */
  'trip_not_open',
  /** [409] `optionId` is not the traveller's current choice (EC-54). */
  'option_not_selected',
  /** [409] The booking's trip has neither an approved change nor a cancellation to cancel for. */
  'cancel_not_approved',
  /** [409] No refund is pending on this booking. */
  'refund_not_pending',
  /** [409] A vendor with this type and name already exists. */
  'vendor_exists',
  /** [409] A credit with this reference already exists for the traveller. */
  'credit_reference_exists',
  /** [409] The amendment was already decided. */
  'amendment_not_pending',
  /** [409] The trip already has an amendment waiting for a decision. */
  'amendment_pending',
  /** [422] The vendor is deactivated: it is not offered for new bookings (EC-55). */
  'vendor_inactive',
  /** [422] The vendor does not exist in this organisation. */
  'vendor_not_found',
  /** [422] The credit cannot be applied (not the traveller's, empty, or in another currency). */
  'credit_not_usable',
  /** [422] The credit has expired (EC-17). */
  'credit_expired',
  /** [422] The credit belongs to a different vendor than the booking. */
  'credit_vendor_mismatch',
  /** [422] An option's `expiresAt` is not in the future. */
  'option_expiry_in_past',
  /** [422] The cancellation charge is more than the booking paid. */
  'charge_exceeds_amount',
  /** [422] A refund is more than the booking paid. */
  'refund_exceeds_amount',
  /** [422] The refund's received date is in the future. */
  'received_in_future',
  /** [422] A shared booking's members are not all one kind. */
  'shared_kind_mismatch',
  /** [422] A shared booking's members are not all one legal entity (one company GSTIN). */
  'shared_entity_mismatch',
  /** [422] A shared booking's members are not all in one currency. */
  'shared_currency_mismatch',
  /** [422] The declared or detected file type is not PDF, JPEG or PNG. */
  'file_type_not_allowed',
  /** [422] The file is empty or over the size limit. */
  'file_size_invalid',
  /** [422] The trip already holds the maximum number of documents. */
  'document_limit',
  /** [422] The booking named does not belong to this trip. */
  'booking_not_on_trip',
  /** [422] An amendment names a leg or stay that is not on the trip. */
  'unknown_itinerary_item',
  /** [422] The policy does not let this traveller book that mode themselves (D1). */
  'self_booking_not_allowed',
  /** [422] A shortened or extended trip's new end date is not valid. */
  'bad_end_date',
  /** [422] An amendment proposes nothing. */
  'empty_proposal',
] as const;

export type TravelDeskErrorCode = (typeof TRAVEL_DESK_ERROR_CODES)[number];

/**
 * The money codes (Phase 4: advances from a trip, trip-linked claims, allowance drafts, settlement
 * rounds, net-off, recovery and write-off). `travel-settlement.contract.ts` lists, per route, which
 * of them it sends. The extra fields named here sit beside `code` in `details`.
 */
export const TRAVEL_MONEY_ERROR_CODES = [
  // ---- Advance from a trip (TD-4.2, 4.14, 4.15) ----
  /** [409] Only an approved, needs-action or booked trip takes an advance. */
  'trip_not_approved',
  /** [409] The trip already has an advance that is pending, approved or paid out. Carries `advanceId`. */
  'trip_advance_exists',
  /** [422] Above the policy's `maxAdvancePct` of the estimate or its `maxAdvanceAmount`. Carries `limit` and `currency`. */
  'advance_over_policy',
  /** [422] The advance is not in the trip's currency. */
  'advance_currency_mismatch',
  /** [422] The recovery consent (D22) was not accepted. */
  'recovery_consent_required',
  /** [409] The policy blocks a new advance while another one is overdue (EC-37). Carries `advanceReferences`. */
  'advance_blocked_overdue',

  // ---- Trip-linked claims (TD-4.3, 4.12, 4.17) ----
  /** [422] The claim cannot carry this trip (EC-33). Carries `reason`: one of `TRAVEL_CLAIM_LINK_REFUSALS`. */
  'claim_not_linkable',
  /** [409] A trip-linked claim is approved only through the trip's settlement (D9, EC-31). Carries `travelRequestId`. */
  'claim_needs_settlement',

  // ---- Allowance drafts (TD-4.4, 4.16) ----
  /** [422] The policy in force has no per-diem rule or no rate for the traveller's grade. */
  'per_diem_not_configured',
  /** [409] This allowance was already claimed for the trip (a live claim of that kind exists). Carries `kind`. */
  'allowance_already_claimed',
  /** [422] The policy has no mileage rate for the traveller's grade. */
  'mileage_rate_missing',
  /** [422] The organisation has no active expense category of that kind to file the draft under. Carries `kind`. */
  'expense_category_missing',
  /** [422] Nothing to claim: every day is a personal day, or every amount is zero. */
  'nothing_to_claim',

  // ---- Settlement rounds (TD-4.5, 4.13, 4.18) ----
  /** [409] The main round opens on the trip's last day. */
  'trip_not_ended',
  /** [409] The trip takes no more expenses: closed, the window passed, or the traveller said no more. */
  'settlement_window_closed',
  /** [422] A pre-trip round is only for fares the traveller booked themselves, with self-booking allowed (D29). */
  'pre_trip_not_allowed',
  /** [409] The trip already has a main round that is pending, approved or settled. */
  'main_settlement_exists',
  /** [409] A supplementary round needs an approved main round first. */
  'main_settlement_required',
  /** [409] Another round of this trip is waiting for a decision. */
  'settlement_in_progress',
  /** [409] The round is not pending (decided or withdrawn already). */
  'settlement_not_pending',
  /** [409] Net-off needs an approved round that is not yet settled. */
  'settlement_not_approved',
  /** [422] A claim cannot go in this round: not on the trip, not the traveller's, already in a round, or not a draft. Carries `expenseClaimIds`. */
  'claim_not_settleable',
  /** [422] A per-expense decision names a claim that is not in the round. Carries `expenseClaimIds`. */
  'unknown_settlement_line',
  /** [422] An approved amount is above what was claimed. Carries `expenseClaimIds`. */
  'approved_amount_exceeds_claim',

  // ---- Recovery and write-off (TD-4.6, 4.8, 4.19) ----
  /** [409] The advance has nothing outstanding (not paid out, or already settled). */
  'advance_not_outstanding',
  /** [422] More than is outstanding. */
  'amount_exceeds_outstanding',
  /** [422] Payroll cannot recover this one automatically. Carries `reason`: one of `RECOVERY_MANUAL_REASONS`. */
  'recovery_manual_only',
  /** [409] A payroll deduction for this advance is already scheduled on an unfinalized run. */
  'recovery_already_scheduled',
  /** [409] The traveller's legal entity has no unlocked payroll run to add the deduction to. */
  'no_open_payroll_run',
  /** [409] Only a finalized payroll deduction, a repayment or a net-off can be reversed, and only once. */
  'recovery_not_reversible',
  /** [409] A write-off for this advance is already waiting for a decision. */
  'write_off_pending',
  /** [409] The write-off was already decided or withdrawn. */
  'write_off_not_pending',

  // ---- Recovery inputs on payroll and the F&F (TD-4.6, 4.19, EC-58/60) ----
  /**
   * [409] A payroll variable input that recovers an expense advance cannot be edited by hand: remove
   * it, or change the recovery from the advance. Carries `advanceId`.
   */
  'advance_recovery_input',
  /**
   * [409] A payroll deduction for this advance sits on a run whose inputs are locked (processing
   * or locked), and it may still be taken in full, so the balance may not drop below it. Finish
   * (finalize) or reopen that run, then try again. Sent by every movement that lowers the balance:
   *   - a repayment (`recordRepayment`), a write-off proposal and its approval;
   *   - a net-off of a settlement round against the advance (only when it RACES a run locking:
   *     a net-off that already fits beside the deduction goes through);
   *   - an expense claim settled against the advance (race only, as for net-off);
   *   - closing an advance (`closeAdvance` on the expense side);
   *   - the F&F, which cannot take the advance while its deduction is in flight (recalculate after).
   * Carries `advanceId` and, from the balance checks, `inFlight` (the locked deduction, a decimal
   * string in the advance currency). The F&F sends `advances`: `{ id, reference }[]` instead of
   * `advanceId`, since one F&F can be blocked by several advances.
   */
  'advance_payroll_recovery_in_flight',
  /**
   * [409] A TRIP advance is written off only through the maker-checker write-off proposal
   * (`advance_write_off` workflow, TD-4.8), never closed by hand from the expense side. Sent by
   * `closeAdvance`. Carries `travelRequestId`.
   */
  'use_write_off_workflow',
  /**
   * [409] One or more of the leaver's outstanding advances cannot be deducted in the F&F (wrong
   * currency, nothing paid out, ...). Carries `advances`: `{ id, reference }[]`.
   */
  'advance_not_settleable_in_fnf',
  /**
   * [409] An advance's outstanding amount changed after the F&F was prepared: reject it and prepare
   * a new one. Carries `advances`: the changed advances' labels.
   */
  'fnf_advances_changed',
] as const;

export type TravelMoneyErrorCode = (typeof TRAVEL_MONEY_ERROR_CODES)[number];

/**
 * Why a claim cannot be linked to a trip (EC-33, D32), sent as `reason` beside
 * `claim_not_linkable`.
 */
export const TRAVEL_CLAIM_LINK_REFUSALS = [
  /** The claim is not the traveller's. */
  'other_person',
  /** The expense date is outside the trip dates ± `TRAVEL_CLAIM_WINDOW_DAYS` (pre-trip fares excepted). */
  'outside_trip_dates',
  /** The trip is closed, or in a state that takes no expenses (draft, pending, rejected, ...). */
  'trip_not_open',
] as const;
export type TravelClaimLinkRefusal = (typeof TRAVEL_CLAIM_LINK_REFUSALS)[number];

/**
 * Every machine code a travel route sends in `details.code`: the trip and policy codes, the desk
 * and traveller-side booking codes, then the money codes.
 */
export const TRAVEL_ERROR_CODES = [
  ...TRAVEL_TRIP_ERROR_CODES,
  ...TRAVEL_DESK_ERROR_CODES,
  ...TRAVEL_MONEY_ERROR_CODES,
] as const;

export type TravelErrorCode = (typeof TRAVEL_ERROR_CODES)[number];

export function isTravelErrorCode(value: unknown): value is TravelErrorCode {
  return typeof value === 'string' && (TRAVEL_ERROR_CODES as readonly string[]).includes(value);
}
